import { createReducer, on } from "@ngrx/store";
import { IVesselTransitStatus, IVesselVisit, IVesselVisitGridItem } from "../model/vessel-visit";
import {
  LoadVesselVisits,
  UpdateSelectedVesselVisit,
  UpdateSelectedVesselActivitiesIndices,
  UpdateVesselActivitiesBeingEdited,
  ExitEditNOAMode,
  LoadInputPPAData,
  LoadAgentOrganization,
  UpdateOriginalSelectedVesselVisit
} from "../actions/vesselVisit.action";
import { IServiceApplicationTypes, IVesselActivity, IVesselActivityPurposeDropdown, VesselActivityType } from "../model";
import { IVesselMovement } from "../../vessel-movement/model/vessel-movement";
import { ILocationForNOA, ILocationType, IOrganization, IOrganizationType, IVessel, IVesselOrientation,ICargoType, ICargoUnitType, ICargoOperation } from "@pmis3/master-data-core";
import { ILadenStatusDropdown } from "../../vessel-stop";
import { VesselActivityIndicesAndStopType } from 'libs/vessel-activity/vessel-activity-core/src/lib/vessel-visit/model/vessel-activity';

export enum VesselVisitStateStatus {
  LOADING = "loading vessel visits",
  FAILED = "loading vessel visits failed",
  PENDING = "pending vessel visits",
  SUCCESS = "loading vessel visits success",
  UPDATE_SELECTED_VESSEL_VISIT_SUCCESS = "update selected vessel visit success",
  UPDATE_SELECTED_VESSEL_ACTIVITIES_INDICES_SUCCESS = "update indices of selected vessel activities success",
  UPDATE_EDITING_VESSEL_ACTIVITIES_SUCCESS = "update editing vessel activities success",
  EXIT_EDIT_NOA_MODE = "exit edit NOA mode",
  LOAD_PPA_DATA_SUCCESS = "load PPA data success"
}
export interface VesselVisitState {
  error?: string;
  status: VesselVisitStateStatus;

  vesselVisits: IVesselVisitGridItem[];
  originalSelectedVesselVisit?: IVesselVisit;
  selectedVesselVisit?: IVesselVisit;
  vesselVisitIsDirty?: boolean;
  selectedVesselActivitiesIndices: VesselActivityIndicesAndStopType[];
  editingVesselActivities?: IVesselActivity[];

  purposes: IVesselActivityPurposeDropdown[];
  ladenStatuses: ILadenStatusDropdown[];
  locations: ILocationForNOA[];
  terminals: ILocationForNOA[];
  organizations: IOrganization[];
  organizationTypes: IOrganizationType[];
  vessels: IVessel[];
  locationTypes: ILocationType[];
  vesselOrientations: IVesselOrientation[];
  serviceApplicationTypes: IServiceApplicationTypes[];
  agentOrganization?: IOrganization;
  cargoTypes: ICargoType[];
  cargoUnitTypes: ICargoUnitType[];
  cargoOperations: ICargoOperation[];
  transitStatuses: IVesselTransitStatus[];
}

export const initialVesselVisitState: VesselVisitState = {
  status: VesselVisitStateStatus.PENDING,
  vesselVisits: [],
  selectedVesselActivitiesIndices: [],
  purposes: [],
  ladenStatuses: [],
  locations: [],
  terminals: [],
  locationTypes: [],
  organizations: [],
  organizationTypes: [],
  vessels: [],
  vesselOrientations: [],
  serviceApplicationTypes: [],
  cargoTypes: [],
  cargoUnitTypes: [],
  cargoOperations: [],
  transitStatuses: [],
}

function addStartTimesToMovements<T extends { vesselActivities?: IVesselActivity[] }>(vv: T): T {
  if (!vv?.vesselActivities?.length) return vv;

  const updated = vv.vesselActivities.map((activity, index, all) => {
    if (activity.activityType === VesselActivityType.Movement) {
      const prev = index > 0 ? all[index - 1] : null;
      const prevEstimatedEndTime = (prev as IVesselMovement | null)?.estimatedEndTime;
      const prevActualEndTime = (prev as IVesselMovement | null)?.endTime;

      return {
        ...activity,
        estimatedStartTime: prevEstimatedEndTime ?? (activity as IVesselMovement).estimatedStartTime,
        startTime: prevActualEndTime ?? (activity as IVesselMovement).startTime
      };
    }

    return activity;
  });

  return { ...vv, vesselActivities: updated };
}

export const vesselVisitReducer = createReducer(
  initialVesselVisitState,
  on(LoadVesselVisits, (state) => ({ ...state, status: VesselVisitStateStatus.LOADING })),
  on(UpdateOriginalSelectedVesselVisit, (state, { vesselVisit }) => {
    const vesselVisitWithStartTimes = addStartTimesToMovements(vesselVisit);
    return {
      ...state,
      originalSelectedVesselVisit: vesselVisitWithStartTimes,
      selectedVesselVisit: vesselVisitWithStartTimes,
      editingVesselActivities: vesselVisitWithStartTimes.vesselActivities,
      selectedVesselActivitiesIndices: [],
      vesselVisitIsDirty: false,
      status: VesselVisitStateStatus.UPDATE_SELECTED_VESSEL_VISIT_SUCCESS
    };
  }),
  on(UpdateSelectedVesselVisit, (state, { selectedVesselVisit }) => {
    const vesselVisitWithStartTimes = addStartTimesToMovements(selectedVesselVisit);
    return {
      ...state,
      selectedVesselVisit: vesselVisitWithStartTimes,
      vesselVisitIsDirty: true,
      status: VesselVisitStateStatus.UPDATE_SELECTED_VESSEL_VISIT_SUCCESS,
    };
  }),
  on(UpdateSelectedVesselActivitiesIndices, (state, { indices: indices }) => ({
    ...state,
    status: VesselVisitStateStatus.UPDATE_SELECTED_VESSEL_ACTIVITIES_INDICES_SUCCESS,
    selectedVesselActivitiesIndices: indices,
  })),
  on(UpdateVesselActivitiesBeingEdited, (state, { vesselActivities: vesselActivities, editingIndices: indices }) => ({
    ...state,
    status: VesselVisitStateStatus.UPDATE_EDITING_VESSEL_ACTIVITIES_SUCCESS,
    editingVesselActivities: vesselActivities,
    vesselVisitIsDirty: true,
    selectedVesselActivitiesIndices: indices ?? state.selectedVesselActivitiesIndices
  })),
  on(ExitEditNOAMode, (state) => ({
    ...state,
    selectedVesselVisit: undefined,
    selectedVesselActivitiesIndices: [],
    editingVesselActivities: [],
    vesselVisitIsDirty: undefined,
    status: VesselVisitStateStatus.EXIT_EDIT_NOA_MODE
  })),
  on(LoadInputPPAData, (state, payload) => {
    const vesselVisits = payload.vesselVisits?.map(vv => addStartTimesToMovements(vv));
    return {
      ...state,
      ladenStatuses: payload.ladenStatuses,
      purposes: payload.purposes,
      vesselVisits: vesselVisits,
      locations: payload.locations,
      terminals: payload.terminals,
      vesselOrientations: payload.vesselOrientations,
      locationTypes: payload.locationTypes,
      organizations: payload.organizations,
      organizationTypes: payload.organizationTypes,
      vessels: payload.vessels,
      serviceApplicationTypes: payload.serviceApplicationTypes,
      cargoTypes: payload.cargoTypes,
      cargoUnitTypes: payload.cargoUnitTypes,
      cargoOperations: payload.cargoOperations,
      transitStatuses: payload.transitStatuses,
      status: VesselVisitStateStatus.LOAD_PPA_DATA_SUCCESS
    };
  }),
  on(LoadAgentOrganization, (state, { organization: organization }) => ({
    ...state,
    agentOrganization: organization,
    status: VesselVisitStateStatus.SUCCESS
  }))
)